import React, { useEffect, useMemo, useState } from 'react';

import { Button, Callout, Checkbox, Dialog, Field, Select, TextInput } from '../../../ui';
import { LeadFields, LeadSource, SOURCE_LABELS, errorText, importCandidates, parseCsv } from '../../../TeacherHiring/hiringApi';

/** CSV header → lead field. Covers Naukri's and most portals' export column names. */
const COLUMNS: Array<[keyof LeadFields | 'experience', RegExp]> = [
  ['name', /^(full\s*)?name$|candidate\s*name|^name of/i],
  ['phone', /phone|mobile|contact\s*(no|number)|whatsapp/i],
  ['email', /e-?mail/i],
  ['city', /city|location/i],
  ['genreText', /genre|instrument|skill|key\s*skills|subject|what do you teach/i],
  ['experience', /experience|exp\.?/i],
  ['qualification', /qualification|education|degree/i],
  ['portfolioUrl', /portfolio|youtube|video/i],
];

function toLeads(rows: string[][]): { leads: LeadFields[]; mapped: string[] } {
  const [header, ...body] = rows;
  if (!header) return { leads: [], mapped: [] };
  const index = new Map<string, number>();
  header.forEach((h, i) => {
    const match = COLUMNS.find(([key, pattern]) => !index.has(key) && pattern.test(h.trim()));
    if (match) index.set(match[0], i);
  });
  const cell = (row: string[], key: string) => (index.has(key) ? row[index.get(key)!]?.trim() || undefined : undefined);

  const leads = body
    .map((row) => {
      const years = parseInt(cell(row, 'experience') ?? '', 10);
      const portfolio = cell(row, 'portfolioUrl');
      return {
        name: cell(row, 'name') ?? '',
        phone: cell(row, 'phone') ?? '',
        email: cell(row, 'email'),
        city: cell(row, 'city'),
        genreText: cell(row, 'genreText'),
        experienceYears: Number.isFinite(years) && years >= 0 && years <= 70 ? years : undefined,
        qualification: cell(row, 'qualification'),
        portfolioUrl: portfolio && /^https?:\/\//.test(portfolio) ? portfolio : undefined,
      };
    })
    .filter((lead) => lead.name || lead.phone);
  return { leads, mapped: header.filter((_, i) => Array.from(index.values()).includes(i)) };
}

interface Props {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

const ImportCandidatesDialog: React.FC<Props> = ({ open, onClose, onImported }) => {
  const [rows, setRows] = useState<string[][]>([]);
  const [fileName, setFileName] = useState('');
  const [source, setSource] = useState<LeadSource>('JOB_PORTAL');
  const [sourceDetail, setSourceDetail] = useState('Naukri');
  const [autoScreen, setAutoScreen] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<Awaited<ReturnType<typeof importCandidates>> | null>(null);

  useEffect(() => {
    if (open) {
      setRows([]);
      setFileName('');
      setError(null);
      setResult(null);
    }
  }, [open]);

  const { leads, mapped } = useMemo(() => toLeads(rows), [rows]);

  const onFile = async (file?: File) => {
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setRows(parseCsv(await file.text()));
  };

  const submit = async () => {
    setSaving(true);
    setError(null);
    try {
      setResult(await importCandidates({ source, sourceDetail: sourceDetail.trim() || undefined, autoScreen, rows: leads }));
      onImported();
    } catch (e) {
      setError(errorText(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      persistent
      size="lg"
      title="Import candidates from CSV"
      actions={
        result ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
            <Button onClick={submit} loading={saving} disabled={leads.length === 0 || leads.length > 2000}>
              Import {leads.length || ''} candidates
            </Button>
          </>
        )
      }
    >
      <div className="th-form">
        <p className="ds-text-subtle">
          Export applicants from Naukri (or any job portal) as CSV. Columns for name, phone, email, city, genre or skills,
          experience and qualification are picked up by their header. Anyone already in the pipeline is skipped.
        </p>
        <Field label="CSV file" required>
          {(ctl) => <input {...ctl} className="ds-input" type="file" accept=".csv,text/csv" onChange={(e) => onFile(e.target.files?.[0])} />}
        </Field>
        <div className="th-form__row">
          <Field label="Source">
            {(ctl) => (
              <Select {...ctl} value={source} onChange={(e) => setSource(e.target.value as LeadSource)}
                options={(['JOB_PORTAL', 'OUTBOUND', 'OTHER'] as LeadSource[]).map((s) => ({ value: s, label: SOURCE_LABELS[s] }))} />
            )}
          </Field>
          <Field label="Source detail">{(ctl) => <TextInput {...ctl} value={sourceDetail} onChange={(e) => setSourceDetail(e.target.value)} />}</Field>
        </div>
        <Checkbox checked={autoScreen} onChange={(e) => setAutoScreen(e.target.checked)} label="Start AI screening calls for new candidates" />

        {fileName && !result && (
          <Callout tone={leads.length ? 'info' : 'warning'} title={`${leads.length} rows in ${fileName}`}>
            {mapped.length ? `Using columns: ${mapped.join(', ')}.` : 'No recognisable columns. The first row must be a header with Name and Phone.'}
            {leads.length > 2000 && ' Split the file: at most 2000 rows per import.'}
          </Callout>
        )}
        {result && (
          <Callout tone={result.failed.length ? 'warning' : 'success'} title="Import finished">
            {result.created} added, {result.duplicates} already in the pipeline
            {result.failed.length > 0 && `, ${result.failed.length} skipped:`}
            {result.failed.length > 0 && (
              <ul className="th-list">
                {result.failed.slice(0, 20).map((f) => (
                  <li key={f.row}>Row {f.row} ({f.phone || 'no phone'}): {f.error}</li>
                ))}
              </ul>
            )}
          </Callout>
        )}
        {error && <Callout tone="danger">{error}</Callout>}
      </div>
    </Dialog>
  );
};

export default ImportCandidatesDialog;
